import { ephanApi } from 'api/requests';
import APIErrorsHandlingUtils from 'utils/APIErrorsHandlingUtils';

/**
 * Реєстр e-phan — ADR-0015.
 *
 * Джерело саме ділить записи на реєстр і архів: поле `registryState` каже, де
 * запис лежить (`REGISTRY` — у списку, `BUFFER` — тренер чи клуб, що втратив
 * останнього учасника й дочікує строку, `ARCHIVE` — в архіві). Списки типово
 * віддають `REGISTRY,BUFFER`, а архів треба попросити окремо. Тому тут немає
 * жодного власного правила про те, що вважати чинним: реєстр не передає нічого,
 * архів передає `registryState=ARCHIVE`.
 *
 * Поле `status` (`ACTIVE` / `LIMITED` / `INACTIVE`) відповідає на інше питання —
 * чи сплачений внесок за цю роль, — і тримається окремо для кожної ролі. Запис
 * із простроченим внеском лишається в реєстрі, поки біжить пільговий строк, тож
 * `INACTIVE` у списку — це норма, а не суперечність. Сайт цим полем не
 * користується: членство визначає `registryState`.
 */

/**
 * Підписи класів. Джерело віддає лише номер (`danceClass`), а як його називати
 * українською та англійською, знає тільки продукт. Перелік класів, які реально
 * трапляються, приходить з `/registry/filters` — див. hooks/useRegistryFilters.
 */
export const DANCE_CLASSES = [
  { value: 1, label: 'First steps', labelUa: 'Перші кроки', shortLabel: 'First steps', shortLabelUa: 'Перші кроки', code: 'FirstSteps' },
  { value: 2, label: 'Debut', labelUa: 'Дебют', shortLabel: 'Debut', shortLabelUa: 'Дебют', code: 'Debut' },
  { value: 3, label: 'School', labelUa: 'Школа', shortLabel: 'School', shortLabelUa: 'Школа', code: 'School' },
  { value: 4, label: 'F Class', labelUa: 'F клас', shortLabel: 'F Class', shortLabelUa: 'Рівень F', code: 'F' },
  { value: 5, label: 'E Class', labelUa: 'E клас', shortLabel: 'E Class', shortLabelUa: 'Рівень E', code: 'E' },
  { value: 6, label: 'D Class', labelUa: 'D клас', shortLabel: 'D Class', shortLabelUa: 'Рівень D', code: 'D' },
  { value: 7, label: 'C Class', labelUa: 'C клас', shortLabel: 'C Class', shortLabelUa: 'Рівень C', code: 'C' },
  { value: 8, label: 'B Class', labelUa: 'B клас', shortLabel: 'B Class', shortLabelUa: 'Рівень B', code: 'B' },
  { value: 9, label: 'A Class', labelUa: 'A клас', shortLabel: 'A Class', shortLabelUa: 'Рівень A', code: 'A' },
  { value: 10, label: 'S Class', labelUa: 'S клас', shortLabel: 'S Class', shortLabelUa: 'Рівень S', code: 'S' },
];

/** Підписи суддівських категорій. Джерело так само віддає номер. */
export const JUDGE_CATEGORIES = [
  { value: 1, label: 'Trainee', labelUa: 'Стажер' },
  { value: 2, label: 'Third', labelUa: 'Третя' },
  { value: 3, label: 'Second', labelUa: 'Друга' },
  { value: 4, label: 'First', labelUa: 'Перша' },
  { value: 5, label: 'National', labelUa: 'Національна' },
];

export function resolveDanceClass(value) {
  const level = Number(value);
  if (!level || level < 1 || level > DANCE_CLASSES.length) return null;
  return DANCE_CLASSES[level - 1];
}

export function resolveJudgeCategory(value) {
  const level = Number(value);
  if (!level || level < 1 || level > JUDGE_CATEGORIES.length) return null;
  return JUDGE_CATEGORIES[level - 1];
}

/** Архів — окремий розділ джерела, а не вибірка за станом сплати. */
export const ARCHIVE_STATE = 'ARCHIVE';

/**
 * Параметри, які джерело розуміє. Усе інше не передається: невідомий параметр
 * там мовчки ігнорується, тобто фільтр «спрацював би» на повному списку.
 */
const LIST_PARAMS = [
  'search',
  'country',
  'countryId',
  'city',
  'clubId',
  'coachId',
  'danceClass',
  'highestClass',
  'judgeCategory',
  'position',
  'sex',
  'hasPartner',
  'registryState',
  'sort',
  'order',
  'page',
  'limit',
];

export default class EphanDataService {
  /** Реєстр на сайті — український, тому країна стоїть за замовчуванням. */
  static buildQueryParams(params = {}) {
    const query = new URLSearchParams();
    const withDefaults = { country: 'UA', page: 1, limit: 24, ...params };

    LIST_PARAMS.forEach((key) => {
      const value = withDefaults[key];
      if (value === undefined || value === null || value === '') return;
      query.set(key, String(value));
    });

    return `?${query.toString()}`;
  }

  static async fetchList(endpoint, params = {}) {
    const [errors, response] = await ephanApi.get(`${endpoint}${this.buildQueryParams(params)}`);

    if (errors) {
      return {
        result: [],
        meta: null,
        total: 0,
        page: 1,
        totalPages: 1,
        hasNextPage: false,
        errors: APIErrorsHandlingUtils.handleErrors(errors),
      };
    }

    const data = Array.isArray(response?.data) ? response.data : [];
    const meta = response?.meta || null;

    return {
      result: data,
      meta,
      total: meta?.total ?? data.length,
      page: meta?.page ?? 1,
      totalPages: meta?.totalPages ?? 1,
      hasNextPage: meta?.hasNextPage ?? false,
      errors: [],
    };
  }

  static getDancers(params) {
    return this.fetchList('/dancers', params);
  }

  static getCoaches(params) {
    return this.fetchList('/coaches', params);
  }

  static getJudges(params) {
    return this.fetchList('/judges', params);
  }

  static getClubs(params) {
    return this.fetchList('/clubs', params);
  }

  /**
   * Один клуб за ідентифікатором.
   *
   * Крім усього, що є в переліку, віддає засновання, публічні контакти, всі
   * адреси й тренерський склад — сторінці клубу цього вистачає одним запитом.
   * Танцюристів у відповіді немає навмисно: їх бувають сотні, тож вони
   * лишаються окремим списком (`/dancers?clubId=`).
   *
   * Розділ реєстру тут не питається: архів — це розділ, а не видалення, тож
   * посилання на архівний клуб працює, а `registryState` у відповіді каже, де
   * запис лежить. Клуби не мають слага, тому все, що не UUID, — це 404.
   *
   * @returns {{ result: object|null, notFound: boolean, errors: string[] }}
   */
  static async getClub(clubId) {
    if (!clubId) return { result: null, notFound: true, errors: [] };

    const [errors, response] = await ephanApi.get(`/clubs/${encodeURIComponent(clubId)}`);

    if (errors) {
      const notFound = errors?.response?.status === 404;

      return {
        result: null,
        notFound,
        errors: notFound ? [] : APIErrorsHandlingUtils.handleErrors(errors),
      };
    }

    return { result: response?.data || null, notFound: false, errors: [] };
  }

  /**
   * Значення фільтрів реєстру: країни з лічильниками, наявні класи танцюристів,
   * категорії суддів. Рахуються в тому ж обсязі, у якому віддаються списки, —
   * тобто фільтр не обіцяє записів, яких список не покаже.
   */
  static async getRegistryFilters() {
    const [errors, response] = await ephanApi.get('/registry/filters');

    if (errors) {
      return { result: null, errors: APIErrorsHandlingUtils.handleErrors(errors) };
    }

    return { result: response?.data || null, errors: [] };
  }
}
